/**
 * Client Portal — Dashboard page.
 * Shows overview of released assessments, pending playbacks, available reports.
 * In-app notifications are accessed via the Navbar bell icon.
 * Cards are clickable to navigate to detailed views.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Grid, Paper, Box, CircularProgress, Alert,
} from '@mui/material';
import {
  Assessment, PlayCircle, CloudDownload,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../../api/authFetch';
import PortalTooltip from '../../../components/PortalTooltip';
import RefreshButton from '../../../components/RefreshButton';

interface DashboardStats {
  assessments: number;
  playbackSessions: number;
  reportsAvailable: number;
}

const ClientDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    const safeFetch = (url: string) =>
      authFetch(url).then(r => r.json()).catch(() => ({}));

    Promise.all([
      safeFetch('/api/client/assessments'),
      safeFetch('/api/client/playback-sessions'),
      safeFetch('/api/client/reports'),
    ])
      .then(([assessments, sessions, reports]) => {
        setStats({
          assessments: assessments.assessments?.filter((a: any) => a.released_to_client)?.length || 0,
          playbackSessions: sessions.sessions?.length || 0,
          reportsAvailable: reports.reports?.length || 0,
        });
        setLastRefreshed(new Date());
      })
      .catch(() => setError('Failed to load dashboard data'))
      .finally(() => { setLastRefreshed(new Date()); setLoading(false); });
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  const statCards = [
    { label: 'Released Assessments', value: stats?.assessments || 0, icon: <Assessment fontSize="large" />, color: '#00338D', path: '/client/assessments', tooltipKey: 'client.dashboard.released_assessments' },
    { label: 'Playback Sessions', value: stats?.playbackSessions || 0, icon: <PlayCircle fontSize="large" />, color: '#005EB8', path: '/client/playback', tooltipKey: 'client.dashboard.playback_sessions' },
    { label: 'Reports Available', value: stats?.reportsAvailable || 0, icon: <CloudDownload fontSize="large" />, color: '#0078D4', path: '/client/reports', tooltipKey: 'client.dashboard.reports_available' },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Client Dashboard
        </Typography>
        <RefreshButton onRefresh={fetchData} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>

      <Grid container spacing={3}>
        {statCards.map((card) => (
          <Grid item xs={12} sm={4} key={card.label}>
            <PortalTooltip tooltipKey={card.tooltipKey}>
              <Paper
                elevation={2}
                onClick={() => navigate(card.path)}
                sx={{
                  p: 3, textAlign: 'center', cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 },
                }}
              >
                <Box sx={{ color: card.color, mb: 1 }}>{card.icon}</Box>
                <Typography variant="h3" sx={{ fontWeight: 700, color: card.color }}>
                  {card.value}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="caption" color="primary" sx={{ mt: 1, display: 'block' }}>
                  Click to view →
                </Typography>
              </Paper>
            </PortalTooltip>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ClientDashboard;
